const rowSelector = '.en__component--row';
const slideAnimationDurationMs = 400;
const surveyBlockSelector = '.en__component--svblock';
const surveyProgressBarContainerClass = 'alz-progress-bar';
const surveyProgressBarClass = 'alz-progress-bar-inner';
const surveySelectorQuestionsShowing = '.en__field--survey:not(.en__hidden)';

/**
 * Add progress bar
 */
const addProgressBar = () => {
  const footer = document.querySelector('footer');

  footer?.insertAdjacentHTML(
    'beforebegin',
    `<div class="${surveyProgressBarContainerClass}">
      <progress aria-label="Survey progress" class="${surveyProgressBarClass}" max="100" value="0"></progress>
    </div>`,
  );
};

/**
 * Count the survey questions considered answered so far: questions the user
 * has actually filled in, plus any non-mandatory questions before the last
 * answered mandatory question, since the user must have scrolled past them
 * to reach and answer it.
 */
const countAnsweredSurveyQuestions = (root: ParentNode = document): number => {
  // querySelectorAll returns elements in document order, so this ordering is correct
  // for the "before" comparisons below even when questions sit in different parent
  // containers (e.g. separate .en__component--svblock blocks)
  const allQuestionsShowing = Array.from(
    root.querySelectorAll<HTMLElement>(surveySelectorQuestionsShowing),
  );

  const isMandatory = (question: HTMLElement): boolean =>
    question.classList.contains('en__mandatory');

  // Find the last (highest-index) mandatory question that's actually been answered.
  // Any non-mandatory question before it is treated as answered below, on the
  // assumption that the user must have scrolled past it to reach and answer that
  // mandatory question, whether or not they actually filled it in themselves.
  const lastAnsweredMandatoryIndex = allQuestionsShowing.reduce(
    (lastIndex, question, index) =>
      isMandatory(question) && isQuestionAnswered(question) ? index : lastIndex,
    -1,
  );

  return allQuestionsShowing.filter((question, index) => {
    // Count questions the user has actually filled in themselves
    if (isQuestionAnswered(question)) return true;

    // Otherwise, count unanswered non-mandatory questions that come before an
    // answered mandatory question, regardless of whether they were actually answered
    return !isMandatory(question) && index < lastAnsweredMandatoryIndex;
  }).length;
};

/**
 * Get all rows that come after rows containing a survey block
 */
const getRowsAfterSurveyBlockRows = (): HTMLElement[] => {
  // querySelectorAll returns elements in document order, so the last entry here
  // is the row containing the last survey block
  const surveyBlockRows = Array.from(document.querySelectorAll<HTMLElement>(surveyBlockSelector))
    .map((surveyBlock) => surveyBlock.closest<HTMLElement>(rowSelector))
    .filter((row): row is HTMLElement => row !== null);

  const lastSurveyBlockRow = surveyBlockRows[surveyBlockRows.length - 1];
  if (!lastSurveyBlockRow) return [];

  const rowsAfterSurveyBlockRows: HTMLElement[] = [];
  let sibling = lastSurveyBlockRow.nextElementSibling;

  while (sibling) {
    if (sibling.matches(rowSelector)) rowsAfterSurveyBlockRows.push(sibling as HTMLElement);

    sibling = sibling.nextElementSibling;
  }

  return rowsAfterSurveyBlockRows;
};

/**
 * Returns the mandatory field wrappers (.en__field) that still need
 * a response. An empty array means the block is fully answered.
 * Useful for highlighting incomplete fields in the UI.
 */
const getUnansweredMandatoryFields = (surveyBlock: HTMLElement): HTMLElement[] => {
  const mandatoryFields = Array.from(
    surveyBlock.querySelectorAll<HTMLElement>('.en__field.en__mandatory'),
  ).filter(isVisible); // skip conditionally hidden/not-currently-shown questions

  return mandatoryFields.filter((field) => !isQuestionAnswered(field));
};

const hideAllRowsExceptFirst = () => {
  const rows = document.querySelectorAll<HTMLElement>(rowSelector);

  rows.forEach((row, index) => {
    if (index > 0) row.style.display = 'none';
  });
};

/**
 * Hide progress bar when arriving at content after survey
 */
const hideProgressBarAfterSurveyContent = () => {
  const progressBar = document.querySelector(
    `.${surveyProgressBarClass}`,
  ) as HTMLProgressElement | null;

  const progressBarContainer = document.querySelector(
    `.${surveyProgressBarContainerClass}`,
  ) as HTMLElement | null;

  const contentAfterSurvey = getRowsAfterSurveyBlockRows();

  if (contentAfterSurvey.length > 0) {
    const contentAfterQuestionsIntersectionObserver = new IntersectionObserver((entries) => {
      if (!entries.length || entries[0].intersectionRatio <= 0) return;

      if (progressBar) progressBar.value = 100;

      setTimeout(() => {
        if (progressBarContainer) slideUp(progressBarContainer);
      }, 1000);
    });

    contentAfterQuestionsIntersectionObserver.observe(contentAfterSurvey[0]);
  }
};

/**
 * Determines whether every mandatory question inside a
 * .en__component--svblock container has been answered.
 *
 * Supports text/email/tel/number inputs, textareas, selects,
 * radio groups, and checkbox groups (any combination).
 */
const isBlockComplete = (surveyBlock: HTMLElement): boolean =>
  getUnansweredMandatoryFields(surveyBlock).length === 0;

const isQuestionAnswered = (question: HTMLElement): boolean => {
  const fields = Array.from(
    question.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input, textarea, select',
    ),
  );

  return fields.some((field) => {
    // Checkboxes / radio buttons: answered if at least one is checked
    if (field instanceof HTMLInputElement && (field.type === 'checkbox' || field.type === 'radio'))
      return field.checked;

    // Select dropdowns: answered if a real value is chosen (ignore empty placeholder)
    if (field instanceof HTMLSelectElement) return field.value.trim() !== '';

    // Text inputs, textareas, and any other input types (email, tel, date, number, etc.)
    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)
      return field.value.trim() !== '';

    return false;
  });
};

const isVisible = (el: HTMLElement): boolean => {
  // offsetParent is null when the element (or an ancestor) is display:none,
  // which covers EN's en__hidden convention for conditional questions.
  return el.offsetParent !== null;
};

const isSurveyPage = (): boolean => document.querySelectorAll(surveyBlockSelector).length > 0;

const onNextButtonsClick = () => {
  const nextButtons = document.querySelectorAll<HTMLButtonElement>('button:not([type="submit"])');

  nextButtons.forEach((nextButton) => {
    const row = nextButton.closest(rowSelector) as HTMLElement;
    const surveyBlockInCurrentRow = row.querySelector<HTMLElement>(surveyBlockSelector);
    const followingRow = row.nextElementSibling as HTMLElement | null;

    // Make each survey block next button disabled initially
    if (surveyBlockInCurrentRow) nextButton.disabled = true;

    if (followingRow)
      nextButton.addEventListener('click', () => {
        followingRow.style.display = '';
        scrollToBlock(followingRow);
      });
  });
};

/**
 * Update next button state and progress bar on question answered
 */
const onQuestionsAnswered = () => {
  document.querySelectorAll<HTMLElement>(surveyBlockSelector).forEach((surveyBlock) => {
    const row = surveyBlock.closest<HTMLElement>(rowSelector);
    const nextButton = row?.querySelector<HTMLButtonElement>('button');
    const progressBar = document.querySelector(
      `.${surveyProgressBarClass}`,
    ) as HTMLProgressElement | null;

    surveyBlock.querySelectorAll('input, select, textarea').forEach((element) => {
      element.addEventListener('change', () => {
        // Update next button
        if (nextButton) nextButton.disabled = !isBlockComplete(surveyBlock);

        // Update progress bar
        if (progressBar) {
          const numberOfSurveyQuestions = document.querySelectorAll(
            surveySelectorQuestionsShowing,
          ).length;
          const numberOfAnsweredSurveyQuestions = countAnsweredSurveyQuestions();

          const progressPercent = Math.ceil(
            (numberOfAnsweredSurveyQuestions / numberOfSurveyQuestions) * 100,
          );
          progressBar.value = progressPercent;
        }
      });
    });
  });
};

/**
 * Smoothly scroll to the next survey question or form/text block
 */
const scrollToBlock = (element: HTMLElement) => {
  window.scrollTo({
    top: element.getBoundingClientRect().top + window.scrollY,
    behavior: 'smooth',
  });
};

/**
 * Show/hide progress bar when scrolling to footer (may or may not be after survey is complete)
 */
const showHideProgressBarOnScroll = () => {
  const footer = document.querySelector('footer');
  const progressBarContainer = document.querySelector(
    `.${surveyProgressBarContainerClass}`,
  ) as HTMLElement | null;

  if (footer && progressBarContainer) {
    const footerIntersectionObserver = new IntersectionObserver((entries) => {
      if (!entries.length) return;

      if (entries[0].intersectionRatio > 0) slideUp(progressBarContainer);
      else slideDown(progressBarContainer);
    });

    footerIntersectionObserver.observe(footer);
  }
};

/**
 * Animate an element closed by transitioning its height to zero, then hide it
 *
 * @param {HTMLElement} element
 */
const slideUp = (element: HTMLElement) => {
  if (getComputedStyle(element).display === 'none') return;

  element.style.height = `${element.offsetHeight}px`;
  element.style.overflow = 'hidden';

  // Force a reflow so the browser commits the starting height before the
  // transition is applied, otherwise both style changes can land in the same
  // frame and the height jumps straight to 0 instead of animating smoothly
  void element.offsetHeight;

  element.style.transition = `height ${slideAnimationDurationMs}ms ease`;
  element.style.height = '0';

  setTimeout(() => {
    element.style.display = 'none';
    element.style.removeProperty('height');
    element.style.removeProperty('overflow');
    element.style.removeProperty('transition');
  }, slideAnimationDurationMs);
};

/**
 * Animate an element open by transitioning its height from zero to its natural height
 *
 * @param {HTMLElement} element
 */
const slideDown = (element: HTMLElement) => {
  if (getComputedStyle(element).display !== 'none') return;

  element.style.display = '';
  const targetHeight = element.offsetHeight;
  element.style.height = '0';
  element.style.overflow = 'hidden';

  // Force a reflow so the browser commits the starting height before the
  // transition is applied, otherwise both style changes can land in the same
  // frame and the height jumps straight to its target instead of animating smoothly
  void element.offsetHeight;

  element.style.transition = `height ${slideAnimationDurationMs}ms ease`;
  element.style.height = `${targetHeight}px`;

  setTimeout(() => {
    element.style.removeProperty('height');
    element.style.removeProperty('overflow');
    element.style.removeProperty('transition');
  }, slideAnimationDurationMs);
};

/**
 * If it's a survey page, set up extra functionality for this
 *
 * - Hide next rows until questions in current survey block are answered
 * - Add a progress bar at the bottom of the page
 * - Update question heights so that the survey block height plus the bottom margin always fills at least the screen height
 */
export const survey = () => {
  if (!isSurveyPage()) return;

  // Initially hide all rows except first row

  hideAllRowsExceptFirst();

  // Update survey block heights so that the row height plus the bottom margin always fills the screen height

  updateRowHeights();

  // Add event listeners to next buttons

  onNextButtonsClick();

  // Progress bar

  addProgressBar();

  hideProgressBarAfterSurveyContent();

  showHideProgressBarOnScroll();

  // Update next button state and progress bar on question answered

  onQuestionsAnswered();
};

/**
 * Update question heights so that the question height plus the vertical margins always fills the screen height,
 * with the question vertically centred
 */
const updateRowHeights = () => {
  const setRowHeights = () => {
    document.querySelectorAll<HTMLElement>(rowSelector).forEach((row) => {
      const elementHeight = row.offsetHeight;
      row.style.setProperty('--element-height', `${elementHeight}px`);
    });
  };

  setRowHeights();

  let surveyUpdateQuestionHeightsTimer: ReturnType<typeof setTimeout>;

  window.addEventListener('resize', () => {
    clearTimeout(surveyUpdateQuestionHeightsTimer);
    surveyUpdateQuestionHeightsTimer = setTimeout(setRowHeights, 100);
  });
};
